import React, { Component, createContext } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components/macro'
import media from 'styles/mixins/media'
import uniqueId from 'lodash/uniqueId'
import sortBy from 'lodash/sortBy'
import FlashMessage from './FlashMessage'
import { H4, Body1 } from 'components/layout/text'
import { BasicButton } from 'components/layout/buttons'
import Spacer from 'components/misc/Spacer'

const context = createContext()

const { Provider, Consumer } = context

export { Consumer as ModalConsumer }

const Backdrop = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9998;
  background: black;
  opacity: 0.58;
`

const Wrapper = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.mobile`
    & > :first-child {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
    }
  `}
`

const ModalPosition = styled.div`
  position: relative;
  z-index: 9999;
  width: 46.4rem;
  max-width: calc(100vw - 3.2rem);
`

export class ModalProvider extends Component {

  modals = {}

  getCurrentModal = () => {
    const values = Object.keys(this.modals).map(key => this.modals[key])

    if (!values.length) {
      return null
    }

    const sorted = sortBy(values, ['importance', 'date'])

    const current = sorted[0]

    return current
  }

  addModal = (modal, options) => {
    const date = new Date()
    const id = uniqueId('modal-')
    this.modals[id] = { date, modal, id, ...options }
    this.update()
    return id
  }

  removeModal = id => {
    delete this.modals[id]
    this.update()
  }

  state = this.createState()

  createState() {
    return {
      modals: this.modals,
      addModal: this.addModal,
      removeModal: this.removeModal,
      getCurrentModal: this.getCurrentModal,
    }
  }

  update() {
    this.setState(this.createState())
  }

  render() {
    return (
      <Provider value={this.state}>
        {this.props.children}
      </Provider>
    )
  }
}

class ModalDisplayer extends Component {
  static contextType = context

  componentDidUpdate() {
    if (this.context.getCurrentModal()) {
      this.props.dispatch({ type: 'MODAL_OPEN' })
    } else {
      this.props.dispatch({ type: 'MODAL_CLOSE' })
    }
  }

  render() {
    return (
      <Consumer>
        {manager => {
          const currentModal = manager.getCurrentModal()
          if (currentModal) {
            return (
              <Wrapper>
                <Backdrop onClick={currentModal.onRequestClose} />
                <ModalPosition>
                  <currentModal.wrapper>{currentModal.modal}</currentModal.wrapper>
                </ModalPosition>
              </Wrapper>
            )
          }
        }}
      </Consumer>
    )
  }
}

const ConnectedModalDisplayer = connect()(ModalDisplayer)
export { ConnectedModalDisplayer as ModalDisplayer }

export default class Modal extends Component {
  static contextType = context

  componentDidMount() {
    const id = this.context.addModal(this.props.children, {
      importance: this.props.importance, 
      onRequestClose: this.props.onRequestClose,
      wrapper: this.props.wrapper || (props => <Shadow>{props.children}</Shadow>)
    })
    this.id = id
  }

  componentWillUnmount() {
    this.context.removeModal(this.id)
  }

  render() {
    return null
  }
}


const Shadow = styled.div`
  box-shadow: black 0 0 3px;
  border-radius: 5px;
  display: flex;
  overflow: hidden;
  position: relative;
`

const ModalPanel = styled.div`
  padding: 4rem;
  background: white;
  width: 100%;
`

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
`

const ModalClose = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 1.6rem;
  font-size: 4rem;
  font-weight: bold;
  width: 2rem;
  height: 2.5rem;
  line-height: 2.5rem;
  overflow: hidden;
  cursor: pointer;
  color: ${props => props.theme.colors.greyBase};
`

const LightboxClose = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 4rem;
  font-weight: bold;
  width: 4rem;
  height: 4rem;
  line-height: 2.5rem;
  overflow: hidden;
  cursor: pointer;
  color: white;
  background: rgba(0,0,0,.75);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom-left-radius: 5px;
`


Modal.Panelled = props => <Modal {...props} wrapper={wrapperProps => (
  <Shadow>
    <FlashMessage />
    <ModalPanel>
      {wrapperProps.children}
    </ModalPanel>
    {props.onRequestClose && <ModalClose onClick={props.onRequestClose}>×</ModalClose>}
  </Shadow>
)} />

Modal.Basic = props => (
  <Modal.Panelled {...props}>
    <H4 align="center">{props.title}</H4>
    <Spacer height="1.6rem" />
    {props.children}
    {props.onRequestClose && (
      <>
        <Spacer height="1.6rem" />
        <ButtonWrap>
          <BasicButton onClick={props.onRequestClose}>Dismiss</BasicButton>
        </ButtonWrap>
      </>
    )}
  </Modal.Panelled>
)

Modal.Alert = props => (
  <Modal.Basic {...props}>
    <Body1 align="center">{props.children}</Body1>
  </Modal.Basic>
)

Modal.Lightbox = props => <Modal {...props} wrapper={wrapperProps => (
  <Shadow>
    {wrapperProps.children}
    <LightboxClose onClick={props.onRequestClose}>×</LightboxClose>
  </Shadow>
)} />